import React, {useEffect, useMemo, useState} from "react";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import Utils, {formatPhoneNumber} from "../../../../core/services/utils/utils";
import classnames from "classnames";
import DateUtils from "../../../../core/services/date-utils";
import {useParams} from "react-router-dom";
import {usePartnerInformation} from "../../../hooks/use-partner-information";

const PartSupportRequestPrintView = () => {
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [partSupportRequest, setPartSupportRequest] = useState(null);
    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);
    const [yesCheckedItemsIds, setYesCheckedItemsIds] = useState([]);
    const [noCheckedItemsIds, setNoCheckedItemsIds] = useState([]);
    const isMounted = useIsMounted();

    const partnerInformation = usePartnerInformation();
    const partner = useMemo(() => !partnerInformation ? partnerInformation : ({
        ...partnerInformation,
        logo: (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? '') + (partnerInformation?.logo ?? ''),
    }), [partnerInformation])

    /**
     * Listens to the changes of requestId in the url params:
     * With each change, calls the function for getting the part support information
     */
    useEffect(() => {
        getMakesAndModels().then();
        getPartSupportDetails().then();
        getDriverChecklist().then();
    }, [params.requestId])

    /**
     * Gets makes and models from server
     * @return {Promise<void>}
     */
    const getMakesAndModels = async () => {
        setLoading(true);
        const response = await Api.getAllMakesAndModelsList();
        if (!isMounted()) return;
        setLoading(false);
        if (response?.resultFlag) {
            setMakes(response?.data?.makesList ?? {});
            setModels(response?.data?.modelsList ?? {});
        }
    }


    /**
     * Gets the details of the part support request by calling the related api
     * @return {Promise<void>}
     */
    const getPartSupportDetails = async () => {
        setLoading(true);
        const response = await Api.getPartSupportRequestInformation(params.requestId, true);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPartSupportRequest(response?.data ?? null)
        }
    }

    /**
     * Gets the driver checklist from server and sets the checked items id
     * @return {Promise<void>}
     */
    const getDriverChecklist = async () => {
        const response = await Api.getCurrentDriverCheckList(params.requestId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setYesCheckedItemsIds(response?.data?.values?.filter(e => e.value === 'true')?.map(t => t.driverCheckListId) ?? []);
            setNoCheckedItemsIds(response?.data?.values?.filter(e => e.value === 'false')?.map(t => t.driverCheckListId) ?? []);
        }
    }


    return (
        <>
            <div className={'print-part-support-view'} data-mui-color-scheme="light">
                {
                    loading
                        ? <div className={'min-vh-100 d-flex justify-content-center align-items-center'}>
                            <div className={'loading print-part-support-page'}>
                                <div/>
                            </div>
                        </div>
                        : <div className={'print-part-support-page'}>
                            <table width={'100%'} style={{tableLayout: "fixed"}}>
                                <thead style={{visibility: 'hidden'}}>
                                <tr>
                                    {Array(30).fill(1).map((e, index) =>
                                        <th key={index} colSpan={e} width={`${100 / 30}%`}/>
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {/*TITLE*/}
                                <tr>
                                    <td colSpan={7} rowSpan={2} className={'border-all'}>
                                        <img src={Utils.encodeFileUrl(partner?.logo)}
                                             alt="logo"
                                             width={'100%'}
                                             height={'100%'}
                                        />
                                    </td>
                                    <td colSpan={23} rowSpan={1} className={'border-all no-bottom-border'}>
                                        <p className={'title'}>
                                            RGA Form
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={23} rowSpan={1} className={'border-all no-top-border'}>
                                        <p className={'title-legend'}>
                                            (Return Goods Authorization)
                                        </p>
                                    </td>
                                </tr>
                                {/*CUSTOMER*/}
                                <tr>
                                    <td colSpan={1}
                                        rowSpan={10}
                                        className={'customer-title-wrapper border-all'}
                                    >
                                        <p className={'customer-title'}>
                                            CUSTOMER
                                        </p>
                                    </td>
                                    <td colSpan={6} className={'border-all'}>
                                        <p className={'bold-title'}>
                                            RGA#
                                        </p>
                                    </td>
                                    <td colSpan={16} className={'border-all'}>
                                        <p className={'bold-title-lg under-line'}>
                                            {partSupportRequest?.rga ?? ' '}
                                        </p>
                                    </td>
                                    <td colSpan={3} className={'border-all'}>
                                        <p className={'semi-bold-title'}>
                                            Date
                                        </p>
                                    </td>
                                    <td colSpan={4} className={'border-all'}>
                                        <p className={'large-text'}>
                                            {!!partSupportRequest?.submitDateTime ? DateUtils.serverToLocal(partSupportRequest.submitDateTime).format(DateUtils.dateFormat) : ' '}
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={6} className={'border-all'}>
                                        <p className={'large-text-bold py-4'}>
                                            CUSTOMER NAME
                                        </p>
                                    </td>
                                    <td colSpan={23} className={'border-all'}>
                                        <p className={'bold-title-lg'}>
                                            {partSupportRequest?.customer?.name ?? ' '}
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={6} className={'border-all'}>
                                        <p className={'large-text-bold py-3'}>
                                            ADDRESS
                                        </p>
                                    </td>
                                    <td colSpan={23} className={'border-all'}>
                                        <p className={'large-text-bold text-left py-3'}>
                                            {`${!!partSupportRequest?.customer?.address ? `${partSupportRequest?.customer?.address}, ` : ''}${!!partSupportRequest?.customer?.city ? `${partSupportRequest?.customer?.city}, ` : ''}${!!partSupportRequest?.customer?.postalCode ? `${partSupportRequest?.customer?.postalCode}, ` : ''}`
                                                + `${!!partSupportRequest?.customer?.phone ? `Phone ${formatPhoneNumber(partSupportRequest?.customer?.phone)}` : ''}`}
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Truck Make
                                        </p>
                                    </td>
                                    <td colSpan={10} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Truck Model
                                        </p>
                                    </td>
                                    <td colSpan={5} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Truck Year
                                        </p>
                                    </td>
                                    <td colSpan={7} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Truck VIN
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className={'border-all grey-border'}>
                                        {
                                            !!partSupportRequest?.modelId
                                                ? <p className={'medium-text py-2'}>
                                                    {
                                                        !!partSupportRequest?.modelId
                                                            ? makes?.find(e => e.id === models?.find(t => t.id === partSupportRequest?.modelId)?.makeId)?.name
                                                            : ''
                                                    }
                                                </p>
                                                : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                    <td colSpan={10} className={'border-all grey-border'}>
                                        {
                                            !!partSupportRequest?.modelId
                                                ? <p className={'medium-text py-2'}>
                                                    {
                                                        !!partSupportRequest?.modelId
                                                            ? models?.find(e => e.id === partSupportRequest?.modelId)?.name
                                                            : ''
                                                    }
                                                </p>
                                                : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                    <td colSpan={5} className={'border-all grey-border'}>
                                        {!!partSupportRequest?.year ?
                                            <p className={'medium-text py-2'}>
                                                {partSupportRequest?.year ?? ''}
                                            </p>
                                            : <p className={'medium-text py-4'}/>}
                                    </td>
                                    <td colSpan={7} className={'border-all grey-border'}>
                                        {
                                            !!partSupportRequest?.vin ?
                                                <p className={'medium-text py-2'}>
                                                    {partSupportRequest?.vin ?? ''}
                                                </p>
                                                : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Invoice date/Installed date
                                        </p>
                                    </td>
                                    <td colSpan={10} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Failed Date
                                        </p>
                                    </td>
                                    <td colSpan={5} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Installed Mileage
                                        </p>
                                    </td>
                                    <td colSpan={7} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            Failed Mileage
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className={'border-all grey-border'}>
                                        {
                                            !!partSupportRequest?.installedDate
                                                ? <p className={'medium-text py-2'}>
                                                    {!!partSupportRequest?.installedDate ? DateUtils.serverToLocal(partSupportRequest?.installedDate).format(DateUtils.dateFormat) : ' '}
                                                </p>
                                                : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                    <td colSpan={10} className={'border-all grey-border'}>
                                        {
                                            !!partSupportRequest?.failedDate ?
                                                <p className={'medium-text py-2'}>
                                                    {!!partSupportRequest?.failedDate ? DateUtils.serverToLocal(partSupportRequest?.failedDate).format(DateUtils.dateFormat) : ' '}
                                                </p>
                                                : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                    <td colSpan={5} className={'border-all grey-border'}>
                                        {
                                            !!partSupportRequest?.installedMileage
                                                ? <p className={'medium-text py-2'}>
                                                    {partSupportRequest?.installedMileage ?? ' '}
                                                </p>
                                                : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                    <td colSpan={7} className={'border-all grey-border'}>
                                        {!!partSupportRequest?.failedMileage
                                            ? <p className={'medium-text py-2'}>
                                                {partSupportRequest?.failedMileage ?? " "}
                                            </p>
                                            : <p className={'medium-text py-4'}/>
                                        }
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'large-text-bold py-2'}>
                                            PART#
                                        </p>
                                    </td>
                                    <td colSpan={10} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'large-text-bold py-2'}>
                                            SERIAL#
                                        </p>
                                    </td>
                                    <td colSpan={5} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'large-text-bold py-2'}>
                                            INVOICE#
                                        </p>
                                    </td>
                                    <td colSpan={7} className={'blue-wrapper border-all grey-border'}>
                                        <p className={'large-text-bold py-2'}>
                                            REASON
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className={'border-all grey-border'}>
                                        <p className={'part-number part-number-fluid py-2'}>
                                            {partSupportRequest?.partNo ?? ' '}
                                        </p>
                                    </td>
                                    <td colSpan={10} className={'border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            {partSupportRequest?.serialNo ?? ' '}
                                        </p>
                                    </td>
                                    <td colSpan={5} className={'border-all grey-border'}>
                                        <p className={'medium-text py-2'}>
                                            {partSupportRequest?.invoiceNo ?? ' '}
                                        </p>
                                    </td>
                                    <td colSpan={7} className={'border-all grey-border'}>
                                        <p className={'reason py-2'}>
                                            {partSupportRequest?.type ?? ' '}
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={29} className={'border-right grey-border'}>
                                        <p className={'customer-note'}><span>Note: </span>
                                            Before removing the defective part from vehicle, please take few pictures and video as evidence</p>
                                    </td>
                                </tr>
                                {/*DRIVER*/}
                                <tr>
                                    <td colSpan={1}
                                        rowSpan={5}
                                        className={'border-all'}
                                    >
                                        <p className={'driver-title'}>
                                            DRIVER
                                        </p>
                                    </td>
                                    <td colSpan={9} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-left ml-1'}>
                                            Original Package:
                                        </p>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            Yes
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': yesCheckedItemsIds.includes(1)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            No
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': noCheckedItemsIds.includes(1)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={3} rowSpan={1} className={'border-top border-right py-1'}>
                                        <div className={'w-100'}></div>
                                    </td>
                                    <td colSpan={5} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-left ml-1'}>
                                            Part Damaged:
                                        </p>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            Yes
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': yesCheckedItemsIds.includes(4)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            No
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top border-right py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': noCheckedItemsIds.includes(4)})}/>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={9} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-left ml-1'}>
                                            Box Opened:
                                        </p>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            Yes
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': yesCheckedItemsIds.includes(2)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            No
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': noCheckedItemsIds.includes(2)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={3} rowSpan={1} className={'border-top border-right py-1'}>
                                        <div className={'w-100'}></div>
                                    </td>
                                    <td colSpan={5} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-left ml-1'}>
                                            Box Damaged:
                                        </p>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            Yes
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': yesCheckedItemsIds.includes(3)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            No
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top border-right py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': noCheckedItemsIds.includes(3)})}/>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={9} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-left ml-1'}>
                                            Hardware kit inside the box:
                                        </p>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            Yes
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': yesCheckedItemsIds.includes(5)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={1} rowSpan={1} className={'border-top py-1'}>
                                        <p className={'small-text-bold text-right mr-2'}>
                                            No
                                        </p>
                                    </td>
                                    <td colSpan={2} rowSpan={1} className={'border-top py-1'}>
                                        <div className={'w-100'}>
                                            <div className={classnames('check-box', {'checked': noCheckedItemsIds.includes(5)})}/>
                                        </div>
                                    </td>
                                    <td colSpan={14} rowSpan={1} className={'border-top border-right py-1'}>
                                        <div className={'w-100'}></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={29} className={'border-right'}>
                                        <div className={'w-100'}/>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={5} className={'border-all'}>
                                        <p className={'large-text-bold py-3'}>
                                            REMARKS
                                        </p>
                                    </td>
                                    <td colSpan={24} className={'border-all'}>
                                        <p className={'small-text py-2'}>
                                            {partSupportRequest?.remarks ?? ''}
                                        </p>
                                    </td>
                                </tr>
                                {/*REMARKS*/}
                                <tr>
                                    <td colSpan={1}
                                        rowSpan={6}
                                        className={'border-all'}
                                    >
                                        <p className={'remarks-title'}>
                                            REMARKS
                                        </p>
                                    </td>
                                    <td colSpan={29} className={'border-all'}>
                                        <div className={'remarks-details'}>
                                            <p>
                                                {'For any return or warranty claims, customers must complete this form and fax or email it to the below addresses and get an RGA number before returning any part'}
                                                <br/>
                                                {'Credits will be processed upon receiving the part, and will be issued upon approval'}
                                                <br/>
                                                {'Warranty does not cover: improper installation, storage, maintenance, repair, modification, defects, or failures due to misuse, negligence, accident, physical damage and improper handling.'}
                                                <br/>
                                                {'15% restocking fee and or $35 re-boxing fee may apply on returns.'}
                                                <br/>
                                                {'No return on electrical parts.'}
                                                <br/>
                                                {'Out of town customers may send 5 pictures of the faulty part attached to this form and keep the part for 1 month and get approval before scrapping:'}
                                                <br/>
                                                <br/>
                                                {'Picture#1: complete part- front side'}
                                                <br/>
                                                {'Picture#2: complete part- back side'}
                                                <br/>
                                                {'Picture#3: showing the problem'}
                                                <br/>
                                                {'Picture#4: showing the test result (test tank for leak)'}
                                                <br/>
                                                {'Picture#5: showing the serial number on the part, if any'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                                <tr/>
                                <tr/>
                                <tr/>
                                <tr/>
                                <tr/>
                                {/*SIGNATURES*/}
                                <tr>
                                    <td colSpan={6} className={'border-all'}>
                                        <p className={'large-text-bold'}>
                                            Driver Signature
                                        </p>
                                    </td>
                                    <td colSpan={15} className={'border-all'}>
                                        <div className={'w-100 py-4'}></div>
                                    </td>
                                    <td colSpan={4} className={'border-all'}>
                                        <p className={'large-text-bold'}>
                                            Date
                                        </p>
                                    </td>
                                    <td colSpan={5} className={'border-all'}>
                                        <div className={'w-100'}></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={6} className={'border-all'}>
                                        <p className={'large-text-bold'}>
                                            Customer Signature
                                        </p>
                                    </td>
                                    <td colSpan={15} className={'border-all'}>
                                        <div className={'w-100 py-4'}></div>
                                    </td>
                                    <td colSpan={4} className={'border-all'}>
                                        <p className={'large-text-bold'}>
                                            Date
                                        </p>
                                    </td>
                                    <td colSpan={5} className={'border-all'}>
                                        <div className={'w-100 py-4'}></div>
                                    </td>
                                </tr>
                                {/*PARTNER ADDRESSES*/}
                                <tr>
                                    <td rowSpan={2} colSpan={5} className={'border-all'}>
                                        <p className={'large-text-bold'}>
                                            Address
                                        </p>
                                    </td>
                                    <td rowSpan={1} colSpan={25} className={'border-all rga-value-row'}>
                                        <p className={'large-text text-left py-2 ml-2'}>
                                            #3, 20085, 100A Ave, Langley, BC. V1M3G4 | T 604 888 1883 | F 604 608 4846
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td rowSpan={1} colSpan={25} className={'border-all rga-value-row'}>
                                        <p className={'large-text text-left py-2 ml-2'}>
                                            sia@chtp.ca | warranty@chtp.ca | www.chtp.com
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td rowSpan={2} colSpan={5} className={'border-all'}>
                                        <p className={'large-text-bold'}>
                                            Comments
                                        </p>
                                    </td>
                                    <td rowSpan={1} colSpan={25} className={'border-all rga-value-row'}>
                                        <p className={'large-text text-left py-2 ml-2'}>
                                            {'\u00A0'}
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td rowSpan={1} colSpan={25} className={'border-all rga-value-row'}>
                                        <p className={'large-text text-left py-2 ml-2'}>
                                            {'\u00A0'}
                                        </p>
                                    </td>
                                </tr>
                                {/*BOTTOM INFORMATION*/}
                                <tr className={'border-all'}>
                                    <td colSpan={30} rowSpan={1}>
                                        <div className={'print-footer'}>
                                            <p className={'print-footer-center py-1'}>
                                                {'QC-FR-04'}
                                                <span className={'print-footer-rev'}>Rev. 06</span>
                                                <span className={'print-footer-date'}>July 08, 2026</span>
                                            </p>
                                            <p className={'print-footer-page'}>
                                                Page 1 of 1
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                }
            </div>
        </>
    );
}

export default PartSupportRequestPrintView;
